import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Navigate, useLocation } from 'react-router-dom';
import { safeNextPath } from '@/utils/safeNextPath';
import { useAuthStore } from '@/stores/useAuthStore';
import { isFreshAccount } from '@/lib/freshAccount';
import { isSignupFollowUpInFlight } from '@/lib/signupFollowUp';

// 로딩 컴포넌트 (2초 타임아웃 포함)
export const LoadingScreen: React.FC<{ message?: string; onTimeout?: () => void }> = ({
    message = "로딩 중...",
    onTimeout
}) => {
    const [hasTimedOut, setHasTimedOut] = useState<boolean>(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            console.warn(`⚠️ 로딩 타임아웃: ${message}`);
            setHasTimedOut(true);
            if (onTimeout) {
                onTimeout();
            }
        }, 5000); // 5초 타임아웃으로 연장

        return () => clearTimeout(timer);
    }, [message, onTimeout]);

    if (hasTimedOut) {
        return (
            <div className="min-h-[100dvh] bg-black flex items-center justify-center px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]" role="alertdialog" aria-live="assertive">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm rounded-2xl bg-[#0F0F0F] border border-[#262626] p-5 text-center shadow-[0_12px_40px_rgba(0,0,0,0.5)]">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF3040]/15 ring-1 ring-[#FF3040]/25">
                        <span className="text-[#FF3040] text-xl">⚠️</span>
                    </div>
                    <h2 className="text-base sm:text-lg font-semibold text-white mb-2">로딩 시간 초과</h2>
                    <p className="text-xs sm:text-sm text-[#A8A8A8] mb-5 leading-relaxed">
                        로딩이 지연되고 있습니다. 네트워크 상태를 확인하거나 새로고침 후 다시 시도해주세요.
                    </p>
                    <button onClick={() => window.location.reload()} className="inline-flex items-center justify-center w-full py-3 rounded-xl bg-[#3182F6] text-white text-sm font-semibold hover:bg-[#2563EB] transition-colors">
                        새로고침
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-[100dvh] bg-black flex items-center justify-center px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]" aria-live="polite">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-[#2A2A2A] bg-[#0F0F0F]">
                    <div className="h-8 w-8 rounded-full border-2 border-[#3182F6]/70 border-t-transparent animate-spin" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-white mb-1">{message}</h2>
                <p className="text-xs sm:text-sm text-[#A8A8A8]">잠시만 기다려주세요…</p>
            </motion.div>
        </div>
    );
};

// 보호된 라우트 컴포넌트 (로그인 필요 + 타임아웃 처리)
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isInitialized, isLoading } = useAuthStore();
    const [forcePass, setForcePass] = useState(false);
    const location = useLocation();

    // 타임아웃 핸들러
    const handleTimeout = () => {
        console.warn('⚠️ 인증 로딩 타임아웃 - 강제 통과');
        setForcePass(true);
    };

    // 강제 통과 또는 정상적으로 초기화 완료된 경우
    if (forcePass || isInitialized) {
        // 로그인되지 않은 경우 로그인 페이지로
        if (!user && !forcePass) {
            console.log('🚫 ProtectedRoute: 사용자 없음, 로그인 페이지로 리다이렉트');
            // 로그인 뒤 원래 보려던 화면으로 돌아오게 주소를 넘긴다(카카오 광고 링크 등, 2026-10-06)
            const next = `${location.pathname}${location.search}`;
            return <Navigate to={`/auth?next=${encodeURIComponent(next)}`} replace />;
        }

        // 로그인된 사용자는 바로 접근 허용
        console.log('✅ ProtectedRoute: 접근 허용', user?.id);
        return <>{children}</>;
    }

    // 인증 초기화 중이거나 로딩 중 (2초 타임아웃 포함)
    if (!isInitialized || isLoading) {
        return <LoadingScreen message="인증 확인 중..." onTimeout={handleTimeout} />;
    }

    // 기본적으로 로그인 페이지로 리다이렉트 (돌아올 주소 포함)
    return <Navigate to={`/auth?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />;
};

// 공개 라우트 컴포넌트 (로그인 시 대시보드로 리다이렉트)
export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isInitialized } = useAuthStore();
    const [forcePass, setForcePass] = useState(false);

    // 타임아웃 핸들러
    const handleTimeout = () => {
        console.warn('🚨 공개 라우트 인증 확인 타임아웃 - 강제로 진행합니다');
        setForcePass(true);
    };

    // 강제 통과 또는 정상적으로 초기화 완료된 경우
    if (forcePass || isInitialized) {
        // 이미 로그인된 경우 대시보드로 리다이렉트 — 로그인 화면이 ?next=를 받았으면 그 주소로(로그인 직후 경쟁 방지)
        if (user && !forcePass) {
            // 방금 인증한 새 계정(생성 30분 안)은 뒤처리(추천인·알림톡·동의·접속 정보)를 끝낸 뒤 로그인 화면이 넘긴다 — 2026-10-22:
            // 여기서 먼저 넘기면 배포 직후 옛 화면에서 자동 새로고침이 돌아 뒤처리가 끊겼다(lib/signupFollowUp, 로그인 화면이 최대 5초 뒤 넘김)
            if (isSignupFollowUpInFlight() && isFreshAccount(user.created_at)) {
                return <>{children}</>;
            }
            return <Navigate to={safeNextPath(new URLSearchParams(window.location.search).get('next'))} replace />;
        }

        // 로그인되지 않은 경우 접근 허용
        return <>{children}</>;
    }

    // 인증 초기화 중 (2초 타임아웃 포함)
    if (!isInitialized) {
        return <LoadingScreen message="인증 확인 중..." onTimeout={handleTimeout} />;
    }

    // 기본적으로 접근 허용
    return <>{children}</>;
};
